import { useMemo, useState } from 'react'
import type { EChartsCoreOption } from 'echarts/core'
import { formatMoney, type Snapshot } from '@greencandle/api-client'
import { useECharts } from './echarts'
import { useChartTheme } from './theme'
import { compactMoney } from './format'

/**
 * FR-E2's series, drawn. One line, so there is no legend box - the card title says what
 * is plotted, and a legend with a single swatch would only restate it.
 *
 * A day the backend could not value is a gap in the line, never a zero. Zero would draw a
 * cliff to the axis and read as "the portfolio was worth nothing that day", which is the
 * one thing it does not mean.
 */
export function EquityCurve({
  snapshots,
  currency,
  dimmed,
}: {
  snapshots: Snapshot[]
  currency: string
  dimmed: boolean
}) {
  const theme = useChartTheme()
  const [showTable, setShowTable] = useState(false)

  const option = useMemo<EChartsCoreOption | null>(() => {
    if (snapshots.length === 0) {
      return null
    }

    // Plotting turns a decimal string into a float, which is fine: this number becomes a
    // pixel position and nothing else. Every figure the reader actually reads - tooltip,
    // table, axis - is formatted from the server's string.
    const points = snapshots.map((s) => [
      s.date,
      s.marketValue == null ? null : Number(s.marketValue),
    ])

    const lastIndex = points.length - 1
    const exact = new Map(snapshots.map((s) => [s.date, s]))

    return {
      animation: false,
      // Room for the x-axis band inside the box, so the axis never causes a nested scroll.
      // `containLabel` is registered explicitly: ECharts 6 dropped it from the default build
      // and ignores it silently, which left the axis labels unaccounted for in the layout.
      grid: { left: 8, right: 20, top: 16, bottom: 8, containLabel: true },
      xAxis: {
        type: 'time',
        // Never place two ticks inside the same day. Over a short range ECharts falls to
        // hourly ticks, and with a date-only format that prints "27 Aug" five times in a row.
        minInterval: 24 * 3600 * 1000,
        axisLine: { lineStyle: { color: theme.axis, width: 1 } },
        axisTick: { show: false },
        axisLabel: {
          color: theme.textMuted,
          hideOverlap: true,
          // One consistent format for every tick. ECharts' default mixes month names and
          // bare day numbers on the same axis, which reads as two different scales.
          formatter: (value: number) =>
            new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        scale: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: theme.textMuted,
          formatter: (value: number) => compactMoney(value, currency),
        },
        // Hairline, solid, one step off the surface. Never dashed - a dashed grid reads
        // as a threshold or a projection when it is only a grid.
        splitLine: { lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'line', snap: true, lineStyle: { color: theme.axis, width: 1 } },
        backgroundColor: theme.surface,
        borderColor: theme.grid,
        textStyle: { color: theme.text },
        // Built as DOM rather than an HTML string: values and notes come from the API, and
        // string concatenation into innerHTML is how untrusted text becomes markup.
        formatter: (params: unknown) => {
          const list = Array.isArray(params) ? params : [params]
          const first = list[0] as { axisValue?: number | string } | undefined
          if (!first) {
            return ''
          }
          const iso = new Date(first.axisValue as number).toISOString().slice(0, 10)
          const snapshot = exact.get(iso)

          const root = document.createElement('div')
          const date = document.createElement('div')
          date.style.cssText = `color:${theme.textMuted};font-size:12px;margin-bottom:2px`
          date.textContent = iso
          root.append(date)

          const value = document.createElement('div')
          value.style.cssText = 'font-weight:600;font-size:15px'
          value.textContent =
            snapshot && snapshot.marketValue != null
              ? (formatMoney(snapshot.marketValue, currency) ?? '')
              : 'Not valued'
          root.append(value)

          if (snapshot && !snapshot.complete) {
            const note = document.createElement('div')
            note.style.cssText = `color:${theme.textMuted};font-size:12px;margin-top:4px`
            note.textContent = `Valued ${snapshot.holdingsValued} of ${snapshot.holdingsTotal} holdings`
            root.append(note)
          }
          return root
        },
      },
      series: [
        {
          type: 'line',
          data: points,
          // A gap stays a gap. Connecting across it would draw a value for a day the
          // server explicitly declined to value.
          connectNulls: false,
          smooth: false,
          lineStyle: { width: 2, cap: 'round', join: 'round', color: theme.series[0] },
          itemStyle: { color: theme.series[0] },
          // Only the final point carries a marker, with a 2px ring in the surface colour
          // so it stays legible where it sits on the line.
          showSymbol: true,
          symbol: 'circle',
          symbolSize: (_v: unknown, params: { dataIndex: number }) =>
            params.dataIndex === lastIndex ? 9 : 0,
          emphasis: { scale: false },
          areaStyle: { color: theme.series[0], opacity: 0.1 },
        },
      ],
    }
  }, [snapshots, currency, theme])

  const container = useECharts(option)
  const latest = snapshots.at(-1)

  return (
    <div className="card">
      <div className="chart-head">
        <h2 className="card-title">Portfolio value</h2>
        <div className="chart-head-end">
          <button type="button" onClick={() => setShowTable((v) => !v)} aria-pressed={showTable}>
            {showTable ? 'Chart' : 'Table'}
          </button>
        </div>
      </div>

      {snapshots.length === 0 ? (
        <div className="empty-chart">
          No snapshots in this range. Backfill them with{' '}
          <code>POST /api/accounts/&#123;id&#125;/snapshots/backfill</code>.
        </div>
      ) : showTable ? (
        <SnapshotTable snapshots={snapshots} currency={currency} />
      ) : (
        <>
          <div ref={container} className={`chart-canvas${dimmed ? ' refetching' : ''}`} />
          {latest && (
            <p className="chart-note muted">
              {snapshots.length} day{snapshots.length === 1 ? '' : 's'} to {latest.date}
              {snapshots.some((s) => !s.complete) && ' · some days could not value every holding'}
            </p>
          )}
        </>
      )}
    </div>
  )
}

/**
 * The table twin. Every value the chart draws is reachable here without hovering, which
 * is what keeps the tooltip an enhancement rather than the only way to read the data.
 */
function SnapshotTable({ snapshots, currency }: { snapshots: Snapshot[]; currency: string }) {
  // Newest first: reading a table, the recent days are the ones being looked for.
  const rows = [...snapshots].reverse()
  return (
    <div className="table-scroll" style={{ maxHeight: 280, overflowY: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th className="right">Value</th>
            <th className="right">Cost</th>
            <th className="right">Unrealised</th>
            <th>Coverage</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.date}>
              <td className="num">{s.date}</td>
              <td className="right num">{formatMoney(s.marketValue, currency) ?? 'not valued'}</td>
              <td className="right num">{formatMoney(s.costBasis, currency) ?? '—'}</td>
              <td className="right num">{formatMoney(s.unrealisedPnl, currency) ?? '—'}</td>
              <td className={s.complete ? 'muted' : 'down'}>
                {s.holdingsValued} of {s.holdingsTotal}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
